import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { Game } from '../types/curling';
import { getGame } from '../utils/storage';
import { ArrowLeft, Users } from 'lucide-react';
import { computePlayerScores, computeTeamScore, PlayerScore, SHOT_BUCKETS, SHOT_BUCKET_LABELS } from '../utils/playerScoring';
import { computeGameEndOutcomeRates, computeGamePointsAverages, TeamEndOutcomeRates, TeamPointsAverages } from '../utils/teamPointsStats';

const scoreColor = (value: number | null, scale: number): string => {
  if (value === null) return 'text-slate-500';
  const pct = (value / scale) * 100;
  if (pct >= 80) return 'text-emerald-400';
  if (pct >= 50) return 'text-amber-400';
  return 'text-red-400';
};

export function BRCScoringTeams() {
  const navigate = useNavigate();
  const location = useLocation();
  const { gameId } = useParams<{ gameId: string }>();
  const [game, setGame] = useState<Game | null>(null);

  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;

    getGame(gameId)
      .then(foundGame => {
        if (!cancelled && foundGame) setGame(foundGame);
      })
      .catch(error => console.error('Failed to load BRC Scoring data:', error));

    return () => {
      cancelled = true;
    };
  }, [gameId]);

  if (!game) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 p-6 flex items-center justify-center">
        <p className="text-blue-300">Loading…</p>
      </div>
    );
  }

  const players = computePlayerScores(game);
  const team1Players = players.filter(p => p.team === 'team1');
  const team2Players = players.filter(p => p.team === 'team2');
  const team1Score = computeTeamScore(team1Players);
  const team2Score = computeTeamScore(team2Players);
  const pointsAverages = computeGamePointsAverages(game);
  const endOutcomeRates = computeGameEndOutcomeRates(game);

  const renderTeamCard = (
    teamName: string,
    score: ReturnType<typeof computeTeamScore>,
    points: TeamPointsAverages,
    outcomes: TeamEndOutcomeRates,
  ) => (
    <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6">
      <h3 className="text-lg font-semibold text-white mb-1">{teamName}</h3>
      <p className="text-xs text-slate-400 mb-4">Team score is the average of its players' scores.</p>
      <div className="flex items-baseline gap-2 mb-4">
        <span className={`text-4xl font-bold ${score.overallScore === null ? 'text-slate-500' : 'text-white'}`}>
          {score.overallScore !== null ? `${score.overallScore.toFixed(0)}%` : '—'}
        </span>
      </div>
      <div className="flex gap-6 mb-4">
        <div>
          <p className="text-xs text-blue-300 mb-1">Weight Control</p>
          <p className={`text-lg font-bold ${scoreColor(score.weightControlScore, 10)}`}>
            {score.weightControlScore !== null ? score.weightControlScore.toFixed(1) : '—'}
            <span className="text-sm text-blue-300 font-normal"> / 10</span>
          </p>
        </div>
        <div>
          <p className="text-xs text-blue-300 mb-1">Curl Control</p>
          <p className={`text-lg font-bold ${scoreColor(score.curlControlScore, 10)}`}>
            {score.curlControlScore !== null ? score.curlControlScore.toFixed(1) : '—'}
            <span className="text-sm text-blue-300 font-normal"> / 10</span>
          </p>
        </div>
      </div>
      <div className="pt-4 border-t border-white/10">
        <p className="text-xs text-slate-400 mb-2">
          Per end, over {points.endsUsed} end{points.endsUsed === 1 ? '' : 's'}
          {game.isComplete ? ' played' : ' (game setup total)'}
        </p>
        <div className="grid grid-cols-4 gap-3">
          <div>
            <p className="text-xs text-blue-300 mb-1">PFA</p>
            <p className="text-base font-bold text-emerald-400">{points.pfa !== null ? points.pfa.toFixed(2) : '—'}</p>
            <p className="text-[10px] text-slate-500">{points.pointsFor} total</p>
          </div>
          <div>
            <p className="text-xs text-blue-300 mb-1">PGA</p>
            <p className="text-base font-bold text-red-400">{points.pga !== null ? points.pga.toFixed(2) : '—'}</p>
            <p className="text-[10px] text-slate-500">{points.pointsGiven} total</p>
          </div>
          <div>
            <p className="text-xs text-blue-300 mb-1">SPFA</p>
            <p className="text-base font-bold text-teal-400">{points.spfa !== null ? points.spfa.toFixed(2) : '—'}</p>
            <p className="text-[10px] text-slate-500">{points.stolenPointsFor} total</p>
          </div>
          <div>
            <p className="text-xs text-blue-300 mb-1">SPGA</p>
            <p className="text-base font-bold text-rose-400">{points.spga !== null ? points.spga.toFixed(2) : '—'}</p>
            <p className="text-[10px] text-slate-500">{points.stolenPointsGiven} total</p>
          </div>
        </div>
      </div>
      <div className="pt-4 mt-4 border-t border-white/10">
        <p className="text-xs text-slate-400 mb-2">Of {outcomes.playedEnds} end{outcomes.playedEnds === 1 ? '' : 's'} played</p>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <p className="text-xs text-blue-300 mb-1">Hammer Conv.</p>
            <p className="text-base font-bold text-violet-400">
              {outcomes.hammerConversionRate !== null ? `${outcomes.hammerConversionRate.toFixed(0)}%` : '—'}
            </p>
            <p className="text-[10px] text-slate-500">{outcomes.hammerConversions}/{outcomes.hammerEnds} hammer ends</p>
          </div>
          <div>
            <p className="text-xs text-blue-300 mb-1">Force %</p>
            <p className="text-base font-bold text-fuchsia-400">
              {outcomes.forcePercent !== null ? `${outcomes.forcePercent.toFixed(0)}%` : '—'}
            </p>
            <p className="text-[10px] text-slate-500">{outcomes.forcedEnds}/{outcomes.nonHammerEnds} non-hammer ends</p>
          </div>
          <div>
            <p className="text-xs text-blue-300 mb-1">Steal Rate</p>
            <p className="text-base font-bold text-lime-400">
              {outcomes.stealRate !== null ? `${outcomes.stealRate.toFixed(0)}%` : '—'}
            </p>
            <p className="text-[10px] text-slate-500">{outcomes.stolenEnds}/{outcomes.nonHammerEnds} non-hammer ends</p>
          </div>
          <div>
            <p className="text-xs text-blue-300 mb-1">Blank Rate</p>
            <p className="text-base font-bold text-slate-300">
              {outcomes.blankRate !== null ? `${outcomes.blankRate.toFixed(0)}%` : '—'}
            </p>
            <p className="text-[10px] text-slate-500">{outcomes.blankEnds}/{outcomes.playedEnds} ends</p>
          </div>
          <div>
            <p className="text-xs text-blue-300 mb-1">Big-End For</p>
            <p className="text-base font-bold text-emerald-400">
              {outcomes.bigEndRateFor !== null ? `${outcomes.bigEndRateFor.toFixed(0)}%` : '—'}
            </p>
            <p className="text-[10px] text-slate-500">{outcomes.bigEndsFor}/{outcomes.playedEnds} ends</p>
          </div>
          <div>
            <p className="text-xs text-blue-300 mb-1">Big-End Against</p>
            <p className="text-base font-bold text-red-400">
              {outcomes.bigEndRateAgainst !== null ? `${outcomes.bigEndRateAgainst.toFixed(0)}%` : '—'}
            </p>
            <p className="text-[10px] text-slate-500">{outcomes.bigEndsAgainst}/{outcomes.playedEnds} ends</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBucketBreakdown = (teamPlayers: PlayerScore[]) => {
    if (teamPlayers.length === 0) return null;
    return (
      <div className="mb-8 space-y-3">
        <p className="text-sm text-blue-300 font-medium">Weight &amp; Curl by Shot Category</p>
        {teamPlayers.map(player => (
          <div key={player.playerName} className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-xl p-4">
            <p className="text-sm font-semibold text-white mb-3">{player.playerName}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {SHOT_BUCKETS.map(bucket => {
                const b = player.buckets[bucket];
                return (
                  <div key={bucket} className="bg-white/5 border border-white/10 rounded-lg p-3">
                    <p className="text-xs text-blue-300 mb-2">{SHOT_BUCKET_LABELS[bucket]}</p>
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs text-slate-400">Weight</span>
                      <span className={`text-sm font-semibold ${scoreColor(b.weightControlScore, 10)}`}>
                        {b.weightControlScore !== null ? b.weightControlScore.toFixed(1) : '—'}
                        <span className="text-slate-500 font-normal"> ({b.weightShotCount})</span>
                      </span>
                    </div>
                    <div className="flex items-baseline justify-between mt-1">
                      <span className="text-xs text-slate-400">Curl</span>
                      <span className={`text-sm font-semibold ${scoreColor(b.curlControlScore, 10)}`}>
                        {b.curlControlScore !== null ? b.curlControlScore.toFixed(1) : '—'}
                        <span className="text-slate-500 font-normal"> ({b.curlShotCount})</span>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPlayerTable = (teamName: string, teamPlayers: PlayerScore[]) => {
    if (teamPlayers.length === 0) return null;
    return (
      <div className="mb-8">
        <h2 className="text-xl font-bold text-white mb-4">{teamName}</h2>
        <div className="overflow-x-auto">
          <table className="w-full bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 overflow-hidden">
            <thead className="bg-white/5 border-b border-white/15">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-blue-300 uppercase tracking-wider">Player</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Shots</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Weight Control</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Curl Control</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Overall</th>
                <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Traditional</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {teamPlayers.map((player, idx) => (
                <tr key={player.playerName} className={`hover:bg-white/5 ${idx % 2 === 0 ? 'bg-white/5' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-white">{player.playerName}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-white">{player.totalShots}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                    <span className={`font-semibold ${scoreColor(player.weightControlScore, 10)}`}>
                      {player.weightControlScore !== null ? player.weightControlScore.toFixed(1) : '—'}
                    </span>
                    <span className="text-slate-500"> / 10</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                    <span className={`font-semibold ${scoreColor(player.curlControlScore, 10)}`}>
                      {player.curlControlScore !== null ? player.curlControlScore.toFixed(1) : '—'}
                    </span>
                    <span className="text-slate-500"> / 10</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-white">
                    {player.overallScore !== null ? `${player.overallScore.toFixed(0)}%` : '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-blue-300">
                    {player.accuracyPercent !== null ? `${player.accuracyPercent.toFixed(0)}%` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-6">
          <button
            onClick={() => {
              if (location.key !== 'default') {
                navigate(-1);
              } else {
                navigate(`/CurlingCodex/game/${gameId}/brc-scoring`);
              }
            }}
            className="flex items-center gap-2 text-blue-300 hover:text-blue-200 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="flex items-center gap-3 mb-2">
            <Users className="w-6 h-6 text-blue-300" />
            <h1 className="text-2xl font-bold text-white">BRC Scoring - Teams &amp; Players</h1>
          </div>
          <p className="text-sm text-blue-300">
            {game.curlingClub}{game.curlingClub && game.sheetNumber && ' • '}{game.sheetNumber && `Sheet ${game.sheetNumber}`}
          </p>
          <p className="text-xs text-slate-400 mt-2 max-w-3xl">
            Each player is scored on how well they controlled weight and curl relative to what was
            called, then rolled up to a team score. The traditional 0-4 shot-call scale is shown
            alongside for reference only.
          </p>
        </div>

        {players.length === 0 ? (
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-12 text-center">
            <Users className="w-16 h-16 text-blue-300/70 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Shots Recorded Yet</h3>
            <p className="text-blue-300">Player scoring needs at least a few recorded shots to work with.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {renderTeamCard(game.team1Name || 'Team 1', team1Score, pointsAverages.team1, endOutcomeRates.team1)}
              {renderTeamCard(game.team2Name || 'Team 2', team2Score, pointsAverages.team2, endOutcomeRates.team2)}
            </div>

            {renderPlayerTable(game.team1Name || 'Team 1', team1Players)}
            {renderBucketBreakdown(team1Players)}
            {renderPlayerTable(game.team2Name || 'Team 2', team2Players)}
            {renderBucketBreakdown(team2Players)}
          </>
        )}
      </div>
    </div>
  );
}
